import { auth } from '../firebase'; // Import Firebase auth instance

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3800";

/**
 * Fetches a list of jobs from the backend.
 * @param {object} [options={}] - Optional parameters for filtering and pagination.
 * @param {string} [options.fseId] - Filter by franchisee ID.
 * @param {string} [options.clientId] - Filter by Supplier (original Client) ID.
 * @param {string} [options.householdId] - Filter by Client (formerly Household) ID. // Updated comment
 * @param {string} [options.status] - Filter by job status.
 * @param {number} [options.pageSize=10] - The number of jobs to fetch per page.
 * @param {string} [options.startAfterDocId] - The document ID to start fetching after.
 * @param {string} [options.orderBy='jobDate'] - Field to order by.
 * @param {'asc'|'desc'} [options.orderDirection='asc'] - Order direction.
 * @returns {Promise<object>} - The response data containing jobs and pagination info.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const listJobs = async (options = {}) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const {
    fseId = null,
    clientId = null, // Supplier ID
    householdId = null, // Client (formerly Household) ID
    status = null,
    pageSize = 10,
    startAfterDocId = null,
    orderBy = 'jobDate',
    orderDirection = 'asc',
  } = options;

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/job/list`;

    // Build the body object, always include userType and pagination/ordering
    const body = {
        userType: "admin", // Mandatory field
        pageSize,
        orderBy,
        orderDirection
    };
    if (fseId) body.fseId = fseId;
    if (clientId) body.clientId = clientId; // Pass Supplier ID
    if (householdId) body.householdId = householdId; // Pass Client ID (as householdId)
    if (status) body.status = status;
    if (startAfterDocId) body.startAfterDocId = startAfterDocId;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    const responseData = await response.json(); // Read response body once

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
        throw new Error(responseData.message || 'Failed to list jobs');
    }
    // Ensure the response structure matches expectations
    return responseData.data || { jobs: [], nextPageDocId: null };

  } catch (error) {
    console.error("Error listing jobs:", error);
    throw error; // Re-throw the error
  }
};

/**
 * Fetches the details of a specific job.
 * @param {string} jobId - The ID of the job to fetch.
 * @returns {Promise<object>} - The job data.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const getJobDetails = async (jobId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!jobId) {
    throw new Error("Job ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/job/details`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ jobId }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to get job details');
    }

    return responseData.data; // Return the job data object

  } catch (error) {
    console.error(`Error fetching job details for ${jobId}:`, error);
    throw error;
  }
};

/**
 * Updates specific fields of a job by an admin.
 * @param {string} jobId - The ID of the job to update.
 * @param {object} updateData - An object containing the fields to update.
 * @returns {Promise<object>} - The updated job data.
 * @throws {Error} - Throws an error if the update fails or the user is not authenticated.
 */
export const adminUpdateJob = async (jobId, updateData) => {
    const user = auth.currentUser;
    if (!user) {
        throw new Error("User not authenticated.");
    }
    if (!jobId || !updateData || Object.keys(updateData).length === 0) {
        throw new Error("Job ID and update data are required.");
    }

    let response;

    try {
        const idToken = await user.getIdToken();
        const url = `${BASE_URL}/job/admin-update`;

        // Create a shallow copy of updateData to modify
        const cleanedUpdateData = { ...updateData };

        // Remove properties with null values from the update data
        for (const key in cleanedUpdateData) {
            if (cleanedUpdateData[key] === null) {
                delete cleanedUpdateData[key];
            }
        }

        const payload = {
            jobId: jobId,
            ...cleanedUpdateData, // Spread the cleaned fields
            userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        console.log("Sending admin update payload:", JSON.stringify(payload, null, 2));

        response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        const responseData = await response.json();

        if (!response.ok) {
            throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
        }

        if (responseData.status !== 'success') {
            throw new Error(responseData.message || 'Failed to update job via admin');
        }

        return responseData; // Return full response (includes invoiceRctiResults, xeroReconnectRequired, xeroSyncWarnings)

    } catch (error) {
        console.error("Error updating job via admin:", error);
        if (error instanceof SyntaxError && response) {
            try {
                const rawText = await response.text();
                console.error("Raw response text (likely HTML):", rawText);
            } catch (textError) {
                console.error("Could not get raw response text:", textError);
            }
        }
        throw error;
    }
};

/**
 * Updates the status of a specific job.
 * @param {string} jobId - The ID of the job to update.
 * @param {string} status - The new status value (e.g., 'pending-approval').
 * @returns {Promise<object>} - The response data containing the updated job.
 * @throws {Error} - Throws an error if the update fails or the user is not authenticated.
 */
export const updateJobStatus = async (jobId, status) => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated.");
    }
    if (!jobId || !status) {
        throw new Error("Job ID and new status are required.");
    }

    let response; // Define response outside try block to access in catch

    try {
        const idToken = await user.getIdToken();
        const url = `${BASE_URL}/job/update`; // Endpoint for updating

        // --- Corrected payload structure ---
        const payload = {
            jobId: jobId,
            status: status, // Status field directly in the payload
            userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
        // --- End correction ---

        // --- Log the payload before sending ---
        console.log("Sending update payload:", JSON.stringify(payload, null, 2));
        // --- End log ---

        response = await fetch(url, { // Assign to outer response variable
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        // Try to parse JSON first
        const responseData = await response.json();

        if (!response.ok) {
            throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
        }

        if (responseData.status !== 'success') {
            throw new Error(responseData.message || 'Failed to update job status');
        }

        return responseData.data; // Return the updated job data

    } catch (error) {
        console.error("Error updating job status:", error);

        // --- Add this block to log raw response on JSON parse error ---
        if (error instanceof SyntaxError && response) {
            // If it was a JSON parsing error, try to get the raw text
            try {
                const rawText = await response.text();
                console.error("Raw response text (likely HTML):", rawText);
            } catch (textError) {
                console.error("Could not get raw response text:", textError);
            }
        }
        // --- End of added block ---

        throw error; // Re-throw the original error
    }
};

/**
 * Fetches all jobs for a specific schedule.
 * @param {string} scheduleId - The schedule ID.
 * @returns {Promise<Array>} - Array of jobs.
 * @throws {Error} - Throws an error if request fails.
 */
export const getJobsByScheduleId = async (scheduleId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!scheduleId) {
    throw new Error("Schedule ID is required.");
  }

  try {
    const idToken = await user.getIdToken();
    const requestBody = {
      userType: 'admin',
      scheduleId: scheduleId,
      pageSize: 100, // Get all jobs for this schedule
      orderBy: 'jobDate',
      orderDirection: 'asc'
    };
    
    console.log('[getJobsByScheduleId] Request body:', requestBody);
    
    const response = await fetch(`${BASE_URL}/job/list`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();
    console.log('[getJobsByScheduleId] Response:', data);

    if (!response.ok || data.status !== 'success') {
      throw new Error(data.message || 'Failed to fetch jobs');
    }

    const jobs = data.data?.jobs || [];
    console.log(`[getJobsByScheduleId] Returning ${jobs.length} jobs for schedule ${scheduleId}`);
    return jobs;
  } catch (error) {
    console.error("Error fetching jobs by schedule ID:", error);
    throw error;
  }
};

/**
 * Generate invoices for all completed jobs in a monthly allocation.
 * @param {string} scheduleId - The schedule ID.
 * @param {string} monthYear - The month and year (e.g., "November 2025").
 * @returns {Promise<object>} - Response with invoice generation results.
 * @throws {Error} - Throws an error if request fails.
 */
export const generateMonthlyAllocationInvoice = async (scheduleId, monthYear) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/job/generate-monthly-invoice`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ scheduleId, monthYear }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to generate monthly allocation invoice');
    }

    return responseData;
  } catch (error) {
    console.error("Error generating monthly allocation invoice:", error);
    throw error;
  }
};

/**
 * Create invoice and RCTI for a completed job (manual override).
 * This bypasses the automatic creation checks and allows manual creation for completed jobs.
 * 
 * @param {string} jobId - The ID of the job to create invoice/RCTI for.
 * @returns {Promise<Object>} - Response with status (success, partial, fail) and details.
 * @throws {Error} - Throws an error if request fails.
 */
export const createInvoiceAndRcti = async (jobId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/job/create-invoice-rcti`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ jobId }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return responseData;
  } catch (error) {
    console.error("Error creating invoice and RCTI:", error);
    throw error;
  }
};

/**
 * Fetches the audit log entries for a specific job.
 * @param {string} jobId - The ID of the job.
 * @returns {Promise<Array>} - Array of audit log entry objects.
 * @throws {Error} - Throws an error if the fetch fails or the user is not authenticated.
 */
export const getJobAuditLog = async (jobId) => {
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated.");
  if (!jobId) throw new Error("Job ID is required.");

  try {
    const idToken = await user.getIdToken();
    const response = await fetch(`${BASE_URL}/job/audit-log`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ jobId }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to get job audit log');
    }

    return responseData.data?.entries || [];
  } catch (error) {
    console.error(`Error fetching audit log for job ${jobId}:`, error);
    throw error;
  }
};
